import React from 'react';
import { useAudience } from '../contexts/AudienceContext';
import { trustStripContent } from '../data/audienceContent';
import AnimatedMetric from './AnimatedMetric';
import { BrandName } from './BrandName';

const borderClass = {
  blue: 'border-blue-100',
  teal: 'border-teal-100',
};

const TrustStripSection = () => {
  const { audience } = useAudience();
  const content = trustStripContent[audience];
  const banner = content.bannerImage;

  return (
    <section
      className="py-12 md:py-16 border-y border-blue-100/80 bg-gradient-to-b from-white via-sky-50/50 to-teal-50/20"
      aria-label="Trust and social proof"
    >
      <div className="container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-6 md:mb-8">
          <p className="text-sm font-semibold uppercase tracking-wider text-teal-700 mb-3">
            {content.eyebrow}
          </p>
          <div className="flex justify-center">
            <BrandName className="text-base sm:text-lg" />
          </div>
        </div>

        {/* Single wide banner — short on mobile to avoid a tall “vertical box” */}
        <div className="relative mb-8 md:mb-10 max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-lg border border-blue-100/90 h-[9.5rem] sm:h-40 md:h-48 lg:h-52 bg-gradient-to-br from-blue-100 to-teal-100">
          <img
            src={banner.src}
            alt={banner.alt}
            width={1600}
            height={520}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-blue-950/35 via-transparent to-teal-900/15 pointer-events-none"
            aria-hidden
          />
        </div>

        {/* Centered logo row — no uneven flex-wrap holes */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10 md:mb-12 max-w-6xl mx-auto">
          {content.logos.map((logo) => (
            <div
              key={logo.abbr}
              className="flex w-40 sm:w-44 shrink-0 items-center gap-3 rounded-2xl bg-white/95 px-3 py-3 shadow-sm border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300"
            >
              <div className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-teal-600 text-[10px] sm:text-xs font-bold text-white leading-tight text-center px-0.5">
                {logo.abbr}
              </div>
              <div className="min-w-0 text-left">
                <div className="text-[11px] sm:text-xs font-bold text-gray-900 leading-snug line-clamp-2">
                  {logo.name}
                </div>
                <div className="text-[9px] sm:text-[10px] text-gray-500 line-clamp-2">{logo.sub}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 max-w-4xl mx-auto text-center">
          {content.metrics.map((m) => (
            <div
              key={m.label}
              className={`rounded-2xl bg-white/95 backdrop-blur-sm p-4 md:p-5 shadow-sm border ${borderClass[m.border]}`}
            >
              <AnimatedMetric
                value={m.value}
                duration={2000}
                delay={m.delay}
                startFrom={m.startFrom}
                intersectionRootMargin="0px 0px -16% 0px"
                className="block font-display text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent"
              />
              <div className="text-xs sm:text-sm text-gray-600 mt-1 leading-snug">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustStripSection;
